import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';

import { widgetAuthQueryFromSearchParams } from '../widget-lib/widget-auth-query';

import { Button } from '@domcrm/embed-ui';
import { EmbedMessageBlock } from '../widget-ui/embed-message-block';

import {
  fetchPublicSubscriptionPlans,
  type PublicSubscriptionPlanItem
} from '../widget-api/public-subscription-plans';
import { purchaseSubscriptionPlan } from '../widget-api/subscription-purchase';
import { listMySubscriptions, type MySubscriptionItem } from '../widget-api/my-subscriptions-list';
import { freezeSubscription, unfreezeSubscription } from '../widget-api/subscription-freeze';
import { fetchMyPaymentsPage } from '../widget-api/my-payments';
import { ApiError } from '../widget-api/http-client';
import { FutureBookingsNotice, futureBookingsFromApiError, type FutureBookingConflict } from '../widget-ui/future-bookings-notice';
import { useEmbedBootstrap } from '../use-widget-bootstrap';
import { useAsync } from '../widget-hooks/useAsync';
import { useWidgetSeo } from '../widget-hooks/use-widget-seo';
import { PATH } from '../widget-lib/path-routes';

type PlanTab = 'all' | 'subscriptions' | 'certificates' | 'my';

const descBoxClass =
  'mt-2 max-h-40 overflow-y-auto rounded-xl border border-[var(--border)] bg-[var(--bg-soft)] p-3 text-sm leading-relaxed text-[var(--text)] [&_a]:text-[var(--accent)] [&_p]:mb-2 [&_ul]:list-disc [&_ul]:pl-5';

function PlanDescription({ plan }: { plan: PublicSubscriptionPlanItem }) {
  const raw = (plan.description || '').trim();
  if (!raw) return null;
  if (plan.description_format === 'html') {
    return <div className={descBoxClass} dangerouslySetInnerHTML={{ __html: raw }} />;
  }
  return <div className={`${descBoxClass} whitespace-pre-wrap`}>{raw}</div>;
}

function visitsProgress(s: MySubscriptionItem): { used: number; total: number; pct: number } | null {
  if (s.visits_total == null || s.visits_total <= 0) return null;
  const used = Math.min(Math.max(0, Number(s.visits_used ?? 0)), s.visits_total);
  const pct = Math.round((used / s.visits_total) * 100);
  return { used, total: s.visits_total, pct };
}

function subscriptionWarning(s: MySubscriptionItem): boolean {
  const prog = visitsProgress(s);
  const left = prog ? prog.total - prog.used : null;
  const d = (() => {
    if (!s.valid_until) return null;
    try {
      const end = new Date(`${s.valid_until.slice(0, 10)}T23:59:59`);
      return Math.ceil((end.getTime() - Date.now()) / 86400000);
    } catch { return null; }
  })();
  if (left != null && left <= 2) return true;
  if (d != null && d <= 7 && d >= 0) return true;
  return false;
}

function canFreeze(s: MySubscriptionItem): boolean {
  return s.status === 'active' && s.freeze_max_count > 0 && s.freeze_allowed_days > 0 &&
    s.freeze_used_count < s.freeze_max_count && s.freeze_used_days < s.freeze_allowed_days;
}

function canUnfreeze(s: MySubscriptionItem): boolean {
  return s.status === 'frozen';
}

type FreezeConfirm = { sub: MySubscriptionItem; action: 'freeze' | 'unfreeze' };

export default function SubscriptionPlansPage() {
  useWidgetSeo(
    'Абонементы и тарифы',
    'Каталог клубных карт, абонементов и тарифных планов. Подробные условия и покупка онлайн.'
  );
  const [searchParams, setSearchParams] = useSearchParams();
  const widgetAuthQuery = useMemo(
    () => widgetAuthQueryFromSearchParams(searchParams),
    [searchParams]
  );
  const qs = searchParams.toString();
  const suffix = qs ? `?${qs}` : '';

  const {
    orgSlug,
    branchId,
    widgetConfig,
    accessToken,
    bootstrapHint,
    loading: bootLoading,
    error: bootError,
    organizationId
  } = useEmbedBootstrap();

  const effectiveBranchId = useMemo(() => {
    if (!widgetConfig) {
      return branchId;
    }
    if (widgetConfig.branches?.length <= 1) {
      return widgetConfig.branches[0]?.id ?? null;
    }
    return branchId;
  }, [widgetConfig, branchId]);

  const needsBranch =
    widgetConfig?.branches && widgetConfig.branches.length > 1 && branchId == null;

  const { data: plans, loading, error } = useAsync(
    async () => {
      if (!orgSlug || !accessToken) {
        return [];
      }
      return fetchPublicSubscriptionPlans(orgSlug, effectiveBranchId, widgetAuthQuery, accessToken);
    },
    [orgSlug, effectiveBranchId, widgetAuthQuery, accessToken]
  );

  const activeTab: PlanTab = (() => {
    const raw = searchParams.get('tab');
    if (raw === 'subscriptions' || raw === 'certificates' || raw === 'my') return raw;
    return accessToken ? 'my' : 'all';
  })();

  const setTab = useCallback((tab: PlanTab) => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      if (tab === 'all') {
        next.delete('tab');
      } else {
        next.set('tab', tab);
      }
      return next;
    });
  }, [setSearchParams]);

  const hasCertificates = useMemo(
    () => plans != null && plans.some((p) => p.plan_type === 'certificate'),
    [plans]
  );
  const hasSubscriptions = useMemo(
    () => plans != null && plans.some((p) => p.plan_type !== 'certificate'),
    [plans]
  );

  const catalogTabs = useMemo(() => {
    const tabs: { key: PlanTab; label: string }[] = [];
    if (hasSubscriptions) tabs.push({ key: 'subscriptions', label: 'Абонементы' });
    if (hasCertificates) tabs.push({ key: 'certificates', label: 'Сертификаты' });
    return tabs;
  }, [hasSubscriptions, hasCertificates]);

  const filteredPlans = useMemo(() => {
    if (!plans) return [];
    if (activeTab === 'certificates') return plans.filter((p) => p.plan_type === 'certificate');
    if (activeTab === 'subscriptions') return plans.filter((p) => p.plan_type !== 'certificate');
    if (activeTab === 'all') return plans;
    return [];
  }, [plans, activeTab]);

  const { data: mySubs, loading: mySubsLoading, error: mySubsError, reload: mySubsReload } = useAsync(
    async () => {
      if (!accessToken || activeTab !== 'my') return [];
      return listMySubscriptions(accessToken);
    },
    [accessToken, activeTab]
  );

  const activeSubs = useMemo(() => {
    if (!mySubs) return [];
    const rawList = Array.isArray(mySubs) ? mySubs : [];
    const filtered = organizationId != null
      ? rawList.filter((s) => s.organization_id === organizationId)
      : rawList;
    return filtered.filter(
      (s) => s.status === 'active' || s.status === 'frozen'
    );
  }, [mySubs, organizationId]);

  const [purchaseError, setPurchaseError] = useState<string | null>(null);
  const [purchasingId, setPurchasingId] = useState<number | null>(null);
  const [successMsg, setSuccessMsg] = useState<string | null>(null);
  const [paymentStatus, setPaymentStatus] = useState<'pending' | 'succeeded' | 'failed' | null>(null);
  const [paymentPolling, setPaymentPolling] = useState(false);

  const [freezeConfirm, setFreezeConfirm] = useState<FreezeConfirm | null>(null);
  const [freezingId, setFreezingId] = useState<number | null>(null);
  const [freezeError, setFreezeError] = useState<string | null>(null);
  const [freezeConflict, setFreezeConflict] = useState<FutureBookingConflict[] | null>(null);

  const returnUrl =
    typeof window !== 'undefined'
      ? `${window.location.origin}${window.location.pathname}${window.location.search}`
      : null;

  const onFreezeConfirm = useCallback(async () => {
    if (!freezeConfirm || !accessToken) return;
    setFreezingId(freezeConfirm.sub.id);
    setFreezeError(null);
    setFreezeConflict(null);
    try {
      if (freezeConfirm.action === 'freeze') {
        await freezeSubscription(freezeConfirm.sub.id, accessToken);
      } else {
        await unfreezeSubscription(freezeConfirm.sub.id, accessToken);
      }
      setFreezeConfirm(null);
      mySubsReload();
    } catch (e) {
      const conflict = futureBookingsFromApiError(e);
      if (conflict) {
        setFreezeConflict(conflict);
      } else if (e instanceof ApiError) {
        setFreezeError(e.detail);
      } else {
        setFreezeError(e instanceof Error ? e.message : 'Не удалось выполнить операцию');
      }
    } finally {
      setFreezingId(null);
    }
  }, [freezeConfirm, accessToken, mySubsReload]);

  const checkPaymentStatus = useCallback(async () => {
    if (!accessToken) return;
    setPaymentPolling(true);
    try {
      const page = await fetchMyPaymentsPage(accessToken, { limit: 5 });
      const latest = page.items[0];
      if (!latest) return;
      if (latest.status === 'succeeded') {
        setPaymentStatus('succeeded');
      } else if (latest.status === 'failed' || latest.status === 'cancelled') {
        setPaymentStatus('failed');
      } else if (latest.status === 'pending' || latest.status === 'processing') {
        setPaymentStatus('pending');
      }
    } catch {
      // ignore
    } finally {
      setPaymentPolling(false);
    }
  }, [accessToken]);

  useEffect(() => {
    if (searchParams.has('payment')) {
      checkPaymentStatus();
      const interval = setInterval(checkPaymentStatus, 5000);
      return () => clearInterval(interval);
    }
  }, [searchParams, checkPaymentStatus]);

  async function onBuy(plan: PublicSubscriptionPlanItem) {
    setPurchaseError(null);
    setSuccessMsg(null);
    setPaymentStatus(null);
    if (!accessToken) {
      setPurchaseError('Войдите, чтобы оформить покупку.');
      return;
    }
    if (!plan.can_buy) {
      setPurchaseError('Этот план сейчас недоступен для покупки.');
      return;
    }
    setPurchasingId(plan.id);
    const planName = plan.name;
    try {
      const res = await purchaseSubscriptionPlan(plan.id, accessToken, returnUrl);
      if (res.payment_url) {
        const next = new URLSearchParams(searchParams);
        next.set('payment', '1');
        next.set('plan_name', planName);
        setSearchParams(next);
        window.location.assign(res.payment_url);
        return;
      }
      setSuccessMsg(
        plan.plan_type === 'certificate'
          ? 'Сертификат оформлен. Статус можно посмотреть в «Мои абонементы».'
          : 'Абонемент оформлен. Статус можно посмотреть в «Мои абонементы».'
      );
    } catch (e) {
      if (e instanceof ApiError) {
        setPurchaseError(e.detail);
      } else {
        setPurchaseError(e instanceof Error ? e.message : 'Не удалось оформить покупку');
      }
    } finally {
      setPurchasingId(null);
    }
  }

  const contentTabs = useMemo(() => {
    const items: { key: PlanTab; label: string }[] = [];
    if (accessToken) items.push({ key: 'my', label: 'Мои' });
    items.push(...catalogTabs);
    return items;
  }, [accessToken, catalogTabs]);

  if (bootstrapHint && !orgSlug && !accessToken) {
    return (
      <div className="space-y-3 w-full min-w-0">
        <h1 className="text-xl font-semibold tracking-tight">Абонементы и сертификаты</h1>
        <p className="text-sm text-[var(--text-muted)]">{bootstrapHint}</p>
      </div>
    );
  }

  if (bootLoading) {
    return (
      <div className="flex flex-1 flex-col gap-4 bg-[var(--bg-soft)] px-4 pb-3 pt-4">
        <div className="flex items-start justify-between gap-3 pt-1">
          <div className="min-w-0 flex-1">
            <h1 className="text-xl font-bold tracking-tight text-[var(--text)]">Абонементы</h1>
          </div>
        </div>
        <div className="space-y-3">
          {Array.from({ length: 3 }).map((_, i) => (
            <div key={i} className="h-28 animate-pulse rounded-2xl border border-[var(--border)] bg-[var(--bg-elevated)] shadow-sm" />
          ))}
        </div>
      </div>
    );
  }

  if (bootError) {
    return (
      <div className="flex flex-1 flex-col gap-4 bg-[var(--bg-soft)] px-4 pb-3 pt-4">
        <EmbedMessageBlock variant="error">{bootError}</EmbedMessageBlock>
      </div>
    );
  }

  if (needsBranch && widgetConfig) {
    return (
      <div className="flex flex-1 flex-col gap-4 bg-[var(--bg-soft)] px-4 pb-3 pt-4">
        <div className="flex items-start justify-between gap-3 pt-1">
          <div className="min-w-0 flex-1">
            <h1 className="text-xl font-bold tracking-tight text-[var(--text)]">Абонементы</h1>
          </div>
        </div>
        <p className="text-sm text-[var(--text-muted)]">
          Выберите филиал, чтобы показать доступные планы.
        </p>
        <ul className="flex flex-col gap-2">
          {widgetConfig.branches.map((b) => (
            <li key={b.id}>
              <Button
                type="button"
                variant="secondary"
                className="w-full sm:w-auto"
                onClick={() => {
                  setSearchParams((prev) => {
                    const next = new URLSearchParams(prev);
                    next.set('branch_id', String(b.id));
                    return next;
                  });
                }}
              >
                {b.name}
              </Button>
            </li>
          ))}
        </ul>
        <p className="text-sm">
          <Link className="text-[var(--accent)] underline-offset-2 hover:underline" to={`${PATH.schedule}${suffix}`}>
            ← К расписанию
          </Link>
        </p>
      </div>
    );
  }

  const freezeDialog = freezeConfirm ? (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="w-full max-w-sm rounded-2xl bg-[var(--bg-elevated)] p-5 shadow-xl space-y-4">
        <h3 className="text-base font-semibold text-[var(--text)]">
          {freezeConfirm.action === 'freeze' ? 'Заморозить абонемент?' : 'Разморозить абонемент?'}
        </h3>
        <p className="text-sm text-[var(--text-muted)]">
          {freezeConfirm.action === 'freeze'
            ? `Заморозить «${freezeConfirm.sub.plan_name}»? Использовано заморозок: ${freezeConfirm.sub.freeze_used_count} из ${freezeConfirm.sub.freeze_max_count}. Использовано дней: ${freezeConfirm.sub.freeze_used_days} из ${freezeConfirm.sub.freeze_allowed_days}.`
            : `Разморозить «${freezeConfirm.sub.plan_name}»? Текущая заморозка завершится, оставшийся срок абонемента будет продлён.`}
        </p>
        {freezeConflict ? (
          <FutureBookingsNotice bookings={freezeConflict} bookingsHref={`${PATH.bookings}${suffix}`} />
        ) : null}
        {freezeError ? (
          <EmbedMessageBlock variant="error">{freezeError}</EmbedMessageBlock>
        ) : null}
        <div className="flex gap-2 justify-end">
          <Button type="button" variant="secondary" disabled={freezingId !== null}
            onClick={() => { setFreezeConfirm(null); setFreezeError(null); setFreezeConflict(null); }}>
            Отмена
          </Button>
          <Button type="button" disabled={freezingId !== null} onClick={() => void onFreezeConfirm()}>
            {freezingId !== null
              ? 'Выполняется…'
              : freezeConfirm.action === 'freeze'
                ? 'Заморозить'
                : 'Разморозить'}
          </Button>
        </div>
      </div>
    </div>
  ) : null;

  if (!accessToken) {
    return (
      <div className="px-4 py-6">
        <h1 className="text-xl font-semibold tracking-tight">Абонементы</h1>
        <p className="mt-2 text-sm text-[var(--text-muted)]">Абонементы доступны после входа в личный кабинет.</p>
        <Link className="mt-4 inline-flex h-9 items-center rounded-sm bg-[var(--btn-primary-bg)] px-3.5 text-[13px] font-medium text-[var(--btn-primary-fg)]" to={`${PATH.signIn}${suffix}`}>
          Войти
        </Link>
      </div>
    );
  }

  return (
    <div className="flex flex-1 flex-col gap-4 bg-[var(--bg-soft)] px-4 pb-3 pt-4">
      <div className="flex items-start justify-between gap-3 pt-1">
        <div className="min-w-0 flex-1">
          <h1 className="text-xl font-bold tracking-tight text-[var(--text)]">Абонементы</h1>
        </div>
      </div>

      {contentTabs.length > 0 ? (
        <div className="flex gap-1 overflow-x-auto rounded-lg bg-[var(--bg-soft)] p-1 [scrollbar-width:none]">
          {contentTabs.map((tab) => (
            <button
              key={tab.key}
              type="button"
              className={[
                'shrink-0 rounded-md px-3 py-1.5 text-[13px] font-medium transition-colors',
                activeTab === tab.key
                  ? 'bg-[var(--bg-elevated)] text-[var(--text)] shadow-sm'
                  : 'text-[var(--text-muted)] hover:text-[var(--text)]'
              ].join(' ')}
              onClick={() => setTab(tab.key)}
            >
              {tab.label}
            </button>
          ))}
        </div>
      ) : null}

      {/* Мои абонементы */}
      {activeTab === 'my' ? (
        !accessToken ? (
          <div className="flex flex-1 items-center justify-center">
            <p className="text-sm text-[var(--text-muted)]">Войдите, чтобы увидеть абонементы.</p>
          </div>
        ) : mySubsLoading ? (
          <div className="space-y-3">
            {Array.from({ length: 2 }).map((_, i) => (
              <div key={i} className="h-28 animate-pulse rounded-2xl border border-[var(--border)] bg-[var(--bg-elevated)] shadow-sm" />
            ))}
          </div>
        ) : mySubsError ? (
          <EmbedMessageBlock variant="error">{mySubsError}</EmbedMessageBlock>
        ) : activeSubs.length === 0 ? (
          <EmbedMessageBlock variant="info" showIcon={false}>
            У вас пока нет активных абонементов.
          </EmbedMessageBlock>
        ) : (
          <div className="flex flex-col gap-3">
            {activeSubs.map((s) => {
              const prog = visitsProgress(s);
              const warn = subscriptionWarning(s);
              const frozen = s.status === 'frozen';
              return (
                <div
                  key={s.id}
                  className={[
                    'overflow-hidden rounded-2xl bg-[var(--bg-elevated)] p-4 shadow-[var(--shadow-card)]',
                    warn ? 'ring-2 ring-[color-mix(in_srgb,var(--error-text)_40%,var(--border))]' : '',
                    frozen ? 'opacity-80' : ''
                  ].join(' ')}
                >
                  <div className="flex gap-3">
                    <div
                      className={`w-1 shrink-0 self-stretch rounded-full ${frozen ? 'bg-blue-400' : 'bg-[var(--accent)]'}`}
                      aria-hidden
                    />
                    <div className="min-w-0 flex-1 space-y-2">
                      <div className="flex items-start justify-between gap-2">
                        <p className="text-base font-bold text-[var(--text)]">{s.plan_name}</p>
                        <span className="shrink-0 inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-semibold leading-tight bg-sky-100 text-sky-800">
                          {s.plan_type === 'certificate' ? 'Сертификат' : 'Абонемент'}
                        </span>
                      </div>
                      {frozen ? (
                        <span className="inline-flex rounded-full bg-blue-100 px-2 py-0.5 text-[11px] font-medium text-blue-700">
                          ❄ Заморожен
                        </span>
                      ) : null}
                      {prog && !frozen ? (
                        <div>
                          <div className="h-2 overflow-hidden rounded-full bg-[var(--bg-soft)]">
                            <div
                              className="h-full rounded-full bg-[var(--accent)] transition-[width] duration-700 ease-out motion-reduce:transition-none"
                              style={{ width: `${prog.pct}%` }}
                            />
                          </div>
                          <p className="mt-1 text-xs text-[var(--text-muted)]">
                            {prog.used} / {prog.total} занятий
                          </p>
                        </div>
                      ) : null}
                      {s.valid_until ? (
                        <p className="text-xs text-[var(--text-muted)]">
                          {frozen ? 'Срок до' : 'Активен до'}{' '}
                          {(() => {
                            try {
                              return new Date(`${s.valid_until.slice(0, 10)}T12:00:00`)
                                .toLocaleDateString('ru-RU', { day: 'numeric', month: 'long' });
                            } catch { return s.valid_until.slice(0, 10); }
                          })()}
                        </p>
                      ) : null}
                      {warn && !frozen ? (
                        <p className="text-xs font-medium text-[var(--error-text)]">Скоро закончится — продлите или запишитесь.</p>
                      ) : null}
                      <div className="flex flex-wrap gap-2 pt-1">
                        <Link to={`${PATH.schedule}${suffix}`}>
                          <Button type="button" variant="secondary">Записаться</Button>
                        </Link>
                        {canFreeze(s) ? (
                          <Button type="button" variant="secondary"
                            onClick={() => setFreezeConfirm({ sub: s, action: 'freeze' })}>
                            ❄ Заморозить
                          </Button>
                        ) : null}
                        {canUnfreeze(s) ? (
                          <Button type="button" variant="secondary"
                            onClick={() => setFreezeConfirm({ sub: s, action: 'unfreeze' })}>
                            ☀ Разморозить
                          </Button>
                        ) : null}
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )
      ) : (
        /* Каталог */
        <>
          {loading ? (
            <div className="space-y-3">
              {Array.from({ length: 3 }).map((_, i) => (
                <div key={i} className="h-36 animate-pulse rounded-2xl border border-[var(--border)] bg-[var(--bg-elevated)] shadow-sm" />
              ))}
            </div>
          ) : error ? (
            <EmbedMessageBlock variant="error">{error}</EmbedMessageBlock>
          ) : filteredPlans.length === 0 ? (
            <EmbedMessageBlock variant="info" showIcon={false}>
              Нет доступных планов.
            </EmbedMessageBlock>
          ) : (
            <div className="flex flex-col gap-3">
              {filteredPlans.map((plan) => (
                <div
                  key={plan.id}
                  className="rounded-2xl border border-[var(--border)] bg-[var(--bg-elevated)] p-5 shadow-[var(--shadow-card)]"
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0 flex-1">
                      <p className="text-base font-bold text-[var(--text)]">{plan.name}</p>
                      <div className="mt-1 flex flex-wrap items-center gap-2">
                        <span
                          className={
                            'inline-flex rounded-full px-2 py-0.5 text-[11px] font-semibold leading-tight ' +
                            (plan.plan_type === 'certificate'
                              ? 'bg-amber-100 text-amber-800'
                              : 'bg-sky-100 text-sky-800')
                          }
                        >
                          {plan.plan_type === 'certificate' ? 'Сертификат' : 'Абонемент'}
                        </span>
                        <span className="text-xs text-[var(--text-muted)]">
                          {plan.branch_name ?? 'Все филиалы'}
                        </span>
                        {plan.is_archived ? (
                          <span className="text-xs text-[var(--text-muted)]">Архив</span>
                        ) : null}
                      </div>
                    </div>
                    <div className="shrink-0 text-right">
                      <p className="text-2xl font-bold tabular-nums text-[var(--accent)]">
                        {plan.price > 0 ? `${plan.price.toLocaleString('ru-RU')} ₽` : 'Бесплатно'}
                      </p>
                    </div>
                  </div>

                  <div className="mt-3 grid grid-cols-2 gap-3 rounded-xl border border-[var(--border)] bg-[var(--bg-soft)] px-3.5 py-3">
                    {plan.plan_type === 'certificate' && plan.balance_amount != null ? (
                      <div>
                        <p className="text-[11px] text-[var(--text-muted)]">Номинал</p>
                        <p className="mt-0.5 text-sm font-semibold text-[var(--text)]">
                          {plan.balance_amount.toLocaleString('ru-RU')} ₽
                        </p>
                      </div>
                    ) : plan.visits_total != null ? (
                      <div>
                        <p className="text-[11px] text-[var(--text-muted)]">Визитов</p>
                        <p className="mt-0.5 text-sm font-semibold text-[var(--text)]">
                          {plan.visits_total}
                        </p>
                      </div>
                    ) : null}
                    <div>
                      <p className="text-[11px] text-[var(--text-muted)]">Срок действия</p>
                      <p className="mt-0.5 text-sm font-semibold text-[var(--text)]">
                        {plan.validity_days} дн.
                        {plan.validity_start_type === 'first_use'
                          ? ' · с первого использования'
                          : plan.validity_start_type === 'fixed_date'
                            ? ' · с момента покупки'
                            : ''}
                      </p>
                    </div>
                    {plan.active_users_count != null && plan.active_users_count > 0 ? (
                      <div>
                        <p className="text-[11px] text-[var(--text-muted)]">Купили</p>
                        <p className="mt-0.5 text-sm font-semibold text-[var(--text)]">
                          {plan.active_users_count} чел.
                        </p>
                      </div>
                    ) : null}
                  </div>

                  {plan.services && plan.services.length > 0 ? (
                    <div className="mt-3">
                      <p className="text-xs text-[var(--text-muted)]">
                        Входит услуг: {plan.services.length}
                      </p>
                      <div className="mt-1 flex flex-wrap gap-1.5">
                        {plan.services.slice(0, 5).map((s) => (
                          <span
                            key={s.id}
                            className="rounded-md border border-[var(--border)] bg-[var(--bg-soft)] px-2 py-0.5 text-[11px] text-[var(--text-muted)]"
                          >
                            {s.name}
                          </span>
                        ))}
                        {plan.services.length > 5 ? (
                          <span className="rounded-md border border-[var(--border)] bg-[var(--bg-soft)] px-2 py-0.5 text-[11px] text-[var(--text-muted)]">
                            +{plan.services.length - 5}
                          </span>
                        ) : null}
                      </div>
                    </div>
                  ) : null}

                  <PlanDescription plan={plan} />

                  <div className="mt-4 flex flex-wrap items-center gap-2">
                    {accessToken ? (
                      <Button type="button" disabled={!plan.can_buy || purchasingId !== null} onClick={() => void onBuy(plan)}>
                        {purchasingId === plan.id ? 'Оформление…' : plan.price > 0 ? 'Купить' : 'Получить'}
                      </Button>
                    ) : (
                      <Link to={`${PATH.signIn}${suffix}`}
                        className="inline-flex h-10 items-center justify-center rounded-xl bg-[var(--btn-primary-bg)] px-5 text-[13px] font-semibold text-[var(--btn-primary-fg)]">
                        Войти для покупки
                      </Link>
                    )}
                    {!plan.can_buy ? (
                      <span className="text-xs text-[var(--text-muted)]">Недоступно для покупки</span>
                    ) : null}
                  </div>
                </div>
              ))}
            </div>
          )}

          {paymentStatus != null ? (
            paymentPolling ? (
              <EmbedMessageBlock variant="info">Проверяем статус оплаты…</EmbedMessageBlock>
            ) : paymentStatus === 'succeeded' ? (
              <EmbedMessageBlock variant="success">
                План «{searchParams.get('plan_name') || ''}» оплачен!
              </EmbedMessageBlock>
            ) : paymentStatus === 'pending' ? (
              <EmbedMessageBlock variant="info">Ожидаем подтверждения оплаты. Проверьте статус через несколько секунд.</EmbedMessageBlock>
            ) : (
              <EmbedMessageBlock variant="error">Платёж не прошёл. Попробуйте снова или выберите другой способ оплаты.</EmbedMessageBlock>
            )
          ) : null}

          {purchaseError ? (
            <EmbedMessageBlock variant="error">{purchaseError}</EmbedMessageBlock>
          ) : null}
          {successMsg ? (
            <EmbedMessageBlock variant="success">{successMsg}</EmbedMessageBlock>
          ) : null}
        </>
      )}

      {freezeDialog}
    </div>
  );
}
